import { BaseStore } from "@infrastructure/stores/_base.store";
import { YMap } from "@yandex/ymaps3-types";
import { locationManager } from "@telegram-apps/sdk";
import { GeoClient } from "@infrastructure/clients/http/clients/geo.client";
import { catchError } from "@shared/utils/errors/catchError";

type MapLocation = {
  center: [number, number];
  zoom: number;
};

type MapStoreState = {
  map: YMap | null;
  location: MapLocation;
  userLocation: { latitude: number; longitude: number } | null;
  isVisible: boolean;
  hasInitialized: boolean;
};

class MapStoreFactory extends BaseStore<MapStoreState> {
  map: YMap | null = null;
  private _location: MapLocation = {
    center: [37.64, 55.76],
    zoom: 15
  };
  private _userLocation: { latitude: number; longitude: number } | null = null;
  private _isVisible = false;
  private _hasInitialized = false;

  constructor() {
    super();
  }

  protected createState(): MapStoreState {
    return {
      map: this.map,
      location: this._location,
      userLocation: this._userLocation,
      isVisible: this._isVisible,
      hasInitialized: this._hasInitialized
    };
  }

  protected getStateKey(): string {
    return `map:${this.map ? "ready" : "null"}:${this._isVisible ? "visible" : "hidden"}:${this._location.center[0]}:${this._location.center[1]}`;
  }

  /**
   * Устанавливает карту
   */
  setMap(map: YMap | null) {
    this.map = map;
    this.notifyListeners();
  }

  /**
   * Устанавливает локацию карты
   */
  setLocation(location: MapLocation) {
    this._location = location;
    if (this.map) {
      this.map.setLocation(location);
    }
    this.notifyListeners();
  }

  /**
   * Устанавливает локацию пользователя
   */
  setUserLocation(location: { latitude: number; longitude: number } | null) {
    this._userLocation = location;
    this.notifyListeners();
  }

  /**
   * Устанавливает видимость карты
   */
  setVisibility(isVisible: boolean) {
    this._isVisible = isVisible;
    this.notifyListeners();
  }

  /**
   * Устанавливает флаг инициализации
   */
  setInitialized(initialized: boolean) {
    this._hasInitialized = initialized;
    this.notifyListeners();
  }

  /**
   * Получает текущую геолокацию
   */
  async getCurrentLocation(): Promise<{ latitude: number; longitude: number } | null> {
    if (locationManager.requestLocation.isAvailable()) {
      const location = await locationManager.requestLocation();

      const geoClient = new GeoClient();
      void catchError(
        geoClient.sendLocation(location.latitude, location.longitude)
      );
      
      const mapLocation = {
        center: [location.longitude, location.latitude] as [number, number],
        zoom: 15
      };
      this.setLocation(mapLocation);
      this.setUserLocation({ latitude: location.latitude, longitude: location.longitude });
      
      return { latitude: location.latitude, longitude: location.longitude };
    } else if (navigator.geolocation) {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition((v) => {
          const mapLocation = {
            center: [v.coords.longitude, v.coords.latitude] as [number, number],
            zoom: 15
          };
          this.setLocation(mapLocation);
          this.setUserLocation({ latitude: v.coords.latitude, longitude: v.coords.longitude });
          resolve({ latitude: v.coords.latitude, longitude: v.coords.longitude });
        });
      });
    }
    return null;
  }
}

export const MapStore = new MapStoreFactory();

